import { useTranslation } from 'react-i18next'
import { getMediaUrl } from '@/utils/media'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import { Zap, Shield, Clock, Award, ArrowRight, CheckCircle2, ChevronRight, Cpu } from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

function useCounter(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!hasStarted) return
    let startTime: number
    let animationFrame: number
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      setCount(Math.floor(progress * end))
      if (progress < 1) animationFrame = requestAnimationFrame(animate)
    }
    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [end, duration, hasStarted])

  useEffect(() => {
    if (!ref.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return { count, ref }
}

function FeatureCard({ icon: Icon, title, description, delay }: { icon: React.ElementType; title: string; description: string; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="relative p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl overflow-hidden w-full sm:w-auto sm:flex-1 sm:max-w-[300px]"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div
        className="w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"
        style={{
          background: 'linear-gradient(135deg, #00a3cc 0%, #0077b3 100%)',
          boxShadow: '0 10px 30px rgba(0, 163, 204, 0.3)',
        }}
      >
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
      </div>
      <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{title}</h3>
      <p className="text-sm sm:text-base text-white/70 leading-relaxed">{description}</p>
    </motion.div>
  )
}

export function HiseochlorPage() {
  const { t } = useTranslation()
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  const stats = [
    { value: 18, suffix: '+', label: t('hiseochlor.stat1Label') },
    { value: 55, suffix: '', label: t('hiseochlor.stat2Label') },
  ]

  const features = [
    { icon: Clock, title: t('hiseochlor.feature1Title'), description: t('hiseochlor.feature1Text') },
    { icon: Zap, title: t('hiseochlor.feature2Title'), description: t('hiseochlor.feature2Text') },
    { icon: Shield, title: t('hiseochlor.feature3Title'), description: t('hiseochlor.feature3Text') },
    { icon: Award, title: t('hiseochlor.feature4Title'), description: t('hiseochlor.feature4Text') },
  ]

  return (
    <>
      <SEO title={t('hiseochlor.metaTitle')} description={t('hiseochlor.metaDescription')} image={getMediaUrl('products/electrochlorination/hiseochlor1.png')} />

      <section ref={heroRef} style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', marginTop: '-80px', paddingTop: '80px' }}>
        <motion.div style={{ position: 'absolute', inset: 0, y: heroY }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.1)' }}
          >
            <source src={getMediaUrl('foto/HC_top.mp4')} type="video/mp4" />
          </video>
        </motion.div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,31,66,0.88) 0%, rgba(0,59,122,0.75) 50%, rgba(0,31,66,0.82) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,163,204,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,163,204,0.03) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

        <motion.div style={{ opacity: heroOpacity }} className="container-main relative z-10">
          <div style={{ maxWidth: '900px' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '2rem', backgroundColor: 'rgba(0,163,204,0.2)', border: '1px solid rgba(0,163,204,0.3)', marginBottom: '1.5rem' }}>
              <Cpu style={{ width: '16px', height: '16px', color: '#00a3cc' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#00a3cc', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('hiseochlor.pretitle')}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: '1.5rem' }}>{t('hiseochlor.heroTitle')}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, maxWidth: '700px', marginBottom: '2.5rem' }}>{t('hiseochlor.heroSubtitle')}</motion.p>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <LocalizedLink to="/contact"><Button variant="primary" size="lg">{t('buttons.requestQuote')}<ArrowRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} /></Button></LocalizedLink>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <section style={{ padding: '5rem 0', background: 'linear-gradient(135deg, #001f42 0%, #003b7a 100%)' }}>
        <div className="container-main">
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5rem', textAlign: 'center' }}>
            {stats.map((stat, index) => {
              const { count, ref } = useCounter(stat.value)
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                  <span ref={ref} style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 800, background: 'linear-gradient(135deg, #00a3cc 0%, #ffffff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{count.toLocaleString()}{stat.suffix}</span>
                  <p style={{ marginTop: '0.5rem', fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }}>{stat.label}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '4rem', alignItems: 'center' }}>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#00a3cc', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('hiseochlor.technologyLabel')}</span>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.5rem)', fontWeight: 800, color: '#0f172a', marginTop: '0.5rem', marginBottom: '1.5rem' }}>{t('hiseochlor.overviewTitle')}</h2>
              <p style={{ fontSize: '1.125rem', color: '#64748b', lineHeight: 1.7, marginBottom: '1.5rem' }}>{t('hiseochlor.overviewText')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[t('hiseochlor.benefit1'), t('hiseochlor.benefit2'), t('hiseochlor.benefit3'), t('hiseochlor.benefit4')].map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CheckCircle2 style={{ width: '20px', height: '20px', color: '#00a3cc', flexShrink: 0 }} />
                    <span style={{ color: '#334155', fontWeight: 500 }}>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ position: 'relative' }}>
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <img src={getMediaUrl('products/electrochlorination/hiseochlor1.png')} alt="HISEOCHLOR system" className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', background: 'linear-gradient(180deg, #001f42 0%, #003b7a 100%)', position: 'relative', overflow: 'hidden' }}>
        <div className="container-main relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#00a3cc', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('hiseochlor.whyChooseUs')}</span>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: '#ffffff', marginTop: '0.5rem' }}>{t('hiseochlor.featuresTitle')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (<FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} delay={index * 0.1} />))}
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #003b7a 0%, #001f42 100%)', position: 'relative', overflow: 'hidden' }}>
        <div className="container-main relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, color: '#ffffff', marginBottom: '1.5rem' }}>{t('hiseochlor.ctaTitle')}</h2>
            <p style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2.5rem' }}>{t('hiseochlor.ctaText')}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <LocalizedLink to="/contact"><motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}><Button variant="primary" size="lg">{t('buttons.requestQuote')}<ChevronRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} /></Button></motion.div></LocalizedLink>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
